import React, { useState, useRef, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { productService } from '../../services/productService';

function getFallbackPricing(oxfordDirectPrice) {
  const direct = parseFloat(oxfordDirectPrice) || 10;
  const retailMultiplier = 4.5 + Math.random() * 2.5;
  const retail = Math.round(direct * retailMultiplier * 100) / 100;
  const medicare = Math.round(direct * (2.0 + Math.random() * 1.5) * 100) / 100;
  return { retail, medicare };
}

async function fetchAIPricing(medicationName) {
  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 300,
        system: `You are a pharmaceutical pricing analyst. Given a medication name, provide estimated average US monthly pricing. Return ONLY valid JSON with exactly these two numeric fields:
- "retail_price": estimated average monthly US retail pharmacy cash price in USD
- "medicare_price": estimated average monthly Medicare Part D out-of-pocket cost in USD

Base estimates on publicly available generic drug pricing data. These are estimates for informational purposes only.`,
        messages: [{
          role: 'user',
          content: `Estimated average monthly US retail pharmacy price and Medicare Part D cost for: ${medicationName}. Return ONLY JSON.`
        }]
      })
    });

    if (!response.ok) return null;

    const data = await response.json();
    const text = data.content?.map(b => b.type === 'text' ? b.text : '').join('') || '';
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      const retail = parseFloat(parsed.retail_price);
      const medicare = parseFloat(parsed.medicare_price);
      if (retail > 0 && medicare > 0) {
        return { retail, medicare };
      }
    }
    return null;
  } catch (err) {
    console.warn('AI pricing fetch failed:', err.message);
    return null;
  }
}

export function CostCalculator({ onNavigate }) {
  const { brand } = useBrand();
  const [selectedMeds, setSelectedMeds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const handleClickOutside = useCallback((e) => {
    if (searchRef.current && !searchRef.current.contains(e.target)) {
      setShowResults(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setShowResults(true);
    setSearching(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await productService.searchProducts({
          phrase: query.trim(),
          limit: 10
        });
        setSearchResults(results?.hits || []);
      } catch (err) {
        console.warn('Product search failed:', err.message);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  }, []);

  const selectProduct = async (product) => {
    if (selectedMeds.some(m => m.id === product.id)) {
      setSearchQuery('');
      setShowResults(false);
      return;
    }

    const directPrice = parseFloat(product.price?.specialPrice || product.price?.price || 0);

    const newMed = {
      id: product.id,
      name: product.title,
      direct: directPrice,
      retail: 0,
      medicare: 0,
      loadingPricing: true
    };

    setSelectedMeds(prev => [...prev, newMed]);
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);

    const aiPricing = await fetchAIPricing(product.title);
    const pricing = aiPricing || getFallbackPricing(directPrice);

    setSelectedMeds(prev => prev.map(m => {
      if (m.id === product.id) {
        return {
          ...m,
          retail: pricing.retail,
          medicare: pricing.medicare,
          loadingPricing: false,
          usedFallback: !aiPricing
        };
      }
      return m;
    }));
  };

  const removeMed = (medId) => {
    setSelectedMeds(prev => prev.filter(m => m.id !== medId));
  };

  const anyLoading = selectedMeds.some(m => m.loadingPricing);
  const readyMeds = selectedMeds.filter(m => !m.loadingPricing);

  const totals = readyMeds.reduce(
    (acc, med) => ({
      retail: acc.retail + med.retail,
      medicare: acc.medicare + med.medicare,
      direct: acc.direct + med.direct
    }),
    { retail: 0, medicare: 0, direct: 0 }
  );

  const avgOtherCost = (totals.retail + totals.medicare) / 2;
  const monthlySavings = avgOtherCost - totals.direct;
  const annualSavings = monthlySavings * 12;
  const savingsPercent = avgOtherCost > 0 ? Math.round((monthlySavings / avgOtherCost) * 100) : 0;

  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('dashboard')}
          style={{ marginBottom: 24 }}
        >
          Back to Dashboard
        </Button>

        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
          Cost Savings Calculator
        </h1>
        <p style={{ color: '#6B7280', marginBottom: 32 }}>
          Compare {brand.name} direct pricing with retail pharmacy and Medicare Part D costs
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24,
          alignItems: 'start'
        }}>
          {/* Left Column - Medication Selection */}
          <div>
            <Card style={{ marginBottom: 16, position: 'relative', zIndex: 20, overflow: 'visible' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
                Select Medications
              </h2>

              <div ref={searchRef} style={{ position: 'relative' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '2px solid #E5E7EB',
                  borderRadius: 8,
                  padding: '0 12px',
                  background: 'white',
                  transition: 'border-color 0.2s'
                }}>
                  <Icon name="search" size={18} color="#9CA3AF" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => handleSearch(e.target.value)}
                    onFocus={() => {
                      if (searchResults.length > 0) setShowResults(true);
                    }}
                    placeholder="Search for a medication..."
                    style={{
                      flex: 1,
                      padding: '12px 10px',
                      border: 'none',
                      outline: 'none',
                      fontSize: 14,
                      fontFamily: 'Libre Franklin, sans-serif',
                      background: 'transparent'
                    }}
                  />
                  {searching && (
                    <div style={{
                      width: 18,
                      height: 18,
                      border: '2px solid #E5E7EB',
                      borderTopColor: brand.c.accent,
                      borderRadius: '50%',
                      animation: 'costCalcSpin 0.8s linear infinite'
                    }} />
                  )}
                </div>

                {showResults && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: 300,
                    overflowY: 'auto',
                    background: 'white',
                    border: '2px solid #E5E7EB',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 10
                  }}>
                    {searching && searchResults.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                        Searching...
                      </div>
                    )}
                    {!searching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                      <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                        No medications found
                      </div>
                    )}
                    {searchResults.map(product => {
                      const alreadySelected = selectedMeds.some(m => m.id === product.id);
                      return (
                        <div
                          key={product.id}
                          onClick={() => !alreadySelected && selectProduct(product)}
                          style={{
                            padding: '12px 16px',
                            cursor: alreadySelected ? 'default' : 'pointer',
                            borderBottom: '1px solid #F3F4F6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: alreadySelected ? '#F9FAFB' : 'white',
                            opacity: alreadySelected ? 0.6 : 1,
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={e => {
                            if (!alreadySelected) e.currentTarget.style.background = '#F0F9FF';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = alreadySelected ? '#F9FAFB' : 'white';
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                              {product.title}
                            </div>
                            {product.packaging && (
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                                {product.packaging}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', marginLeft: 12 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#10B981' }}>
                              ${parseFloat(product.price?.specialPrice || product.price?.price || 0).toFixed(2)}
                            </div>
                            <div style={{ fontSize: 11, color: '#6B7280' }}>
                              {alreadySelected ? 'Added' : 'Oxford Direct'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
                Search by medication name to compare pricing
              </p>
            </Card>

            {selectedMeds.length > 0 && (
              <Card>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: brand.c.navy }}>
                  Selected Medications ({selectedMeds.length})
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {selectedMeds.map(med => (
                    <div
                      key={med.id}
                      style={{
                        padding: 10,
                        background: '#F9FAFB',
                        borderRadius: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{med.name}</span>
                        {med.loadingPricing && (
                          <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 8 }}>
                            Fetching prices...
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {!med.loadingPricing && (
                          <span style={{ fontSize: 13, color: '#10B981', fontWeight: 600 }}>
                            ${med.direct.toFixed(2)}
                          </span>
                        )}
                        <button
                          onClick={() => removeMed(med.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4
                          }}
                        >
                          <Icon name="x" size={16} color="#9CA3AF" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Cost Comparison */}
          <div>
            {selectedMeds.length === 0 ? (
              <Card>
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Icon name="dollar" size={32} color="#9CA3AF" />
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                    Select Medications
                  </h3>
                  <p style={{ color: '#6B7280', fontSize: 14 }}>
                    Search for medications above to see your potential savings
                  </p>
                </div>
              </Card>
            ) : (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                    Monthly Cost Comparison
                  </h2>

                  {anyLoading && (
                    <div style={{
                      padding: 12,
                      background: '#FFF7ED',
                      borderRadius: 8,
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10
                    }}>
                      <div style={{
                        width: 16,
                        height: 16,
                        border: '2px solid #FDBA74',
                        borderTopColor: '#F97316',
                        borderRadius: '50%',
                        animation: 'costCalcSpin 0.8s linear infinite',
                        flexShrink: 0
                      }} />
                      <span style={{ fontSize: 13, color: '#9A3412' }}>
                        Fetching pricing data...
                      </span>
                    </div>
                  )}

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 12,
                    marginBottom: 24
                  }}>
                    <div style={{
                      padding: 16,
                      background: '#FEE2E2',
                      borderRadius: 8,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#991B1B', marginBottom: 4, fontWeight: 600 }}>
                        Retail Pharmacy
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: '#EF4444' }}>
                        ${totals.retail.toFixed(2)}
                      </div>
                    </div>

                    <div style={{
                      padding: 16,
                      background: '#FEF3C7',
                      borderRadius: 8,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#92400E', marginBottom: 4, fontWeight: 600 }}>
                        Medicare Part D
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: '#F59E0B' }}>
                        ${totals.medicare.toFixed(2)}
                      </div>
                    </div>

                    <div style={{
                      padding: 16,
                      background: '#D1FAE5',
                      borderRadius: 8,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#065F46', marginBottom: 4, fontWeight: 600 }}>
                        {brand.name} Direct
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: '#10B981' }}>
                        ${totals.direct.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    padding: 16,
                    background: '#EFF6FF',
                    borderRadius: 8
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontSize: 14, color: '#1E40AF', marginBottom: 4, fontWeight: 600 }}>
                          Monthly Savings
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 900, color: '#3B82F6' }}>
                          ${monthlySavings > 0 ? monthlySavings.toFixed(2) : '0.00'}
                        </div>
                      </div>
                      {savingsPercent > 0 && (
                        <div style={{
                          padding: '8px 16px',
                          background: brand.c.accent,
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 20,
                          fontWeight: 700
                        }}>
                          {savingsPercent}%
                        </div>
                      )}
                    </div>
                  </div>
                </Card>

                <Card style={{
                  background: `linear-gradient(135deg, ${brand.c.accent}, ${brand.c.navy})`,
                  color: 'white',
                  marginBottom: 16
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon name="trendUp" size={24} color="white" />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>
                        Annual Savings Projection
                      </div>
                      <div style={{ fontSize: 36, fontWeight: 900, fontFamily: "'Merriweather', serif" }}>
                        ${annualSavings > 0 ? annualSavings.toFixed(2) : '0.00'}
                      </div>
                    </div>
                  </div>
                  <p style={{ fontSize: 14, opacity: 0.9, lineHeight: 1.6 }}>
                    By using {brand.name}, you could save an estimated ${annualSavings > 0 ? annualSavings.toFixed(2) : '0.00'} per year
                    compared to the average of retail pharmacy and Medicare Part D pricing.
                    {annualSavings > 0 && ` That's enough for ${Math.floor(annualSavings / 50)} extra copays or several months of groceries!`}
                  </p>
                </Card>

                <div style={{
                  padding: 12,
                  background: '#FFF8F0',
                  border: '1px solid #FED7AA',
                  borderRadius: 8,
                  fontSize: 12,
                  color: '#92400E',
                  lineHeight: 1.5
                }}>
                  <strong>Disclaimer:</strong> Retail pharmacy and Medicare Part D prices shown are
                  average estimates for informational purposes only and may vary by location,
                  pharmacy, insurance plan, and other factors. Please verify actual costs with
                  your pharmacy or insurance provider before making healthcare decisions.
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes costCalcSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
